/**
 * Action dispatch: name → capability, by lookup.
 *
 * This exists so a consumer never grows an if-chain over action names. The
 * console's `onA2UIAction` is exactly that chain — Copy, OpenQuoteReview,
 * DownloadArtifact, SaveQuoteEdit — and adding one more branch per capability
 * is the shape this library is meant to replace.
 *
 * The core knows no capability names. It resolves the surface, refuses when
 * nothing owns the action, and otherwise hands off.
 */
import { noteActionError } from './diagnostics';
import { activeEntry, entryForKey } from './registry';
import { buildSnapshot } from './snapshot';
import type {
  ViewSurfaceAction,
  ViewSurfaceCapability,
  ViewSurfaceCapabilityResult,
} from './types';

/** True when some registered capability owns this action name. */
export function ownsAction(name: string): boolean {
  const entry = activeEntry();
  if (!entry) {
    return false;
  }
  return (entry.registration.capabilities ?? []).some((c) =>
    c.actions.includes(name)
  );
}

function findCapability(
  capabilities: readonly ViewSurfaceCapability[],
  name: string
): ViewSurfaceCapability | undefined {
  return capabilities.find((c) => c.actions.includes(name));
}

/**
 * Resolve the surface an action is for.
 *
 * A `viewKey` is an address, and it is honoured whether or not that surface is
 * the active one. Two surfaces are mounted at once whenever a workspace has
 * sibling tabs, and "the active surface" is the most recently REGISTERED one,
 * which is not the one the user is looking at and not the one an action built
 * elsewhere — by an agent, or by a server-side extraction — was aimed at.
 *
 * Reaching a surface the user cannot see is the point rather than a hazard: a
 * capability is the only thing that knows whether its action is safe to accept
 * while hidden, and every one of them here stages for review rather than
 * saving. A capability that needs to be in front should say so itself.
 *
 * A blank key still falls through to the active surface, which is what an
 * action that cannot name a mount has always done.
 */
function resolveEntry(action: ViewSurfaceAction) {
  return action.viewKey ? entryForKey(action.viewKey) : activeEntry();
}

/**
 * Route an action to the capability that owns it.
 *
 * Refusals are structural and uniform, so a consumer gets the same shape back
 * whether the surface unmounted, the action targets a different surface, or
 * nothing implements it.
 */
export async function dispatchViewSurfaceAction(
  action: ViewSurfaceAction
): Promise<ViewSurfaceCapabilityResult> {
  const entry = resolveEntry(action);
  if (!entry) {
    // An addressed action distinguishes the two: nothing is mounted at all, or
    // that view in particular is not. Both are cleared by navigating, and the
    // hosts that offer a retry key it on the reason.
    if (action.viewKey && activeEntry()) {
      return {
        ok: false,
        reason: 'view-mismatch',
        message: 'That action was for a different view.',
      };
    }
    return {
      ok: false,
      reason: 'no-active-view',
      message: 'That view is no longer open.',
    };
  }

  const capability = findCapability(
    entry.registration.capabilities ?? [],
    action.name
  );
  if (!capability) {
    return {
      ok: false,
      reason: 'unsupported-capability',
      message: `Nothing here handles "${action.name}".`,
    };
  }

  // Guarded, unlike the direct call this replaces. `peekViewSurfaceSnapshot`
  // and `getViewSurfaceScreenData` both wrap `buildSnapshot` and record a
  // throw; this call site did neither, so a builder bug reached the consumer's
  // catch as an opaque failure and `surfaceBrokerStatus().lastError` stayed
  // null — the read path looking healthy while every action died.
  let snapshot;
  try {
    snapshot = buildSnapshot(entry);
  } catch (err) {
    noteActionError(action.name, err);
    return {
      ok: false,
      reason: 'snapshot-failed',
      message: 'The view could not be read, so that action was not attempted.',
    };
  }

  return capability.handle(action, {
    viewId: entry.registration.viewId,
    viewKey: entry.registration.viewKey,
    entityId: entry.registration.entityId,
    snapshot,
  });
}
